export type MultiAnglePresetId =
  | 'custom'
  | 'fisheye'
  | 'tilt'
  | 'front-top'
  | 'front-bottom'
  | 'top-overview'
  | 'back';

export interface AnglePreset {
  id: Exclude<MultiAnglePresetId, 'custom'>;
  label: string;
  azimuth: number;
  elevation: number;
  scaleIndex: number;
  viewNote: string;
  /** 点击该预设时自动开启提示词开关并填入的文本；未设置 = 提示词关闭并清空 */
  promptText?: string;
}

/** 预设表（方案 2.2）：「自定义」不在此列，由参数实时组合 */
export const ANGLE_PRESETS: AnglePreset[] = [
  {
    id: 'fisheye',
    label: '鱼眼视角',
    azimuth: 0,
    elevation: 0,
    scaleIndex: 4,
    viewNote: '极度特写镜头，广角镜头，边缘带有鱼眼畸变效果',
    promptText: '极度特写镜头，广角镜头，边缘带有鱼眼畸变效果',
  },
  {
    id: 'tilt',
    label: '倾斜视角',
    azimuth: 30,
    elevation: -20,
    scaleIndex: 2,
    viewNote: '相机从侧上方倾斜俯视主体',
    promptText: 'dutch angle，tilted frame',
  },
  {
    id: 'front-top',
    label: '正面俯拍',
    azimuth: 0,
    elevation: 45,
    scaleIndex: 2,
    viewNote: '相机位于主体前上方，自上而下俯拍',
  },
  {
    id: 'front-bottom',
    label: '正面仰拍',
    azimuth: 0,
    elevation: -30,
    scaleIndex: 2,
    viewNote: '相机位于主体前下方，自下而上仰拍',
  },
  {
    id: 'top-overview',
    label: '全景俯拍',
    azimuth: 0,
    elevation: 60,
    scaleIndex: 0,
    viewNote: '相机位于主体前上方高空，大角度向下俯瞰全局',
  },
  {
    id: 'back',
    label: '背面视角',
    azimuth: 180,
    elevation: 0,
    scaleIndex: 2,
    viewNote: '相机位于主体正后方拍摄背面，合理补全正面不可见的部分，风格保持一致',
  },
];

/** 水平环绕按钮组（图一 0°~315° 标注，角度域 0°~359°，无负值） */
export const AZIMUTH_BUTTONS: { label: string; value: number }[] = [
  { label: '0°', value: 0 },
  { label: '45°', value: 45 },
  { label: '90°', value: 90 },
  { label: '135°', value: 135 },
  { label: '180°', value: 180 },
  { label: '225°', value: 225 },
  { label: '270°', value: 270 },
  { label: '315°', value: 315 },
];

/** 垂直俯仰按钮组（正值 = 俯拍） */
export const ELEVATION_BUTTONS: { label: string; value: number }[] = [
  { label: '-30°', value: -30 },
  { label: '0°', value: 0 },
  { label: '30°', value: 30 },
  { label: '60°', value: 60 },
];

/** 景别档位名称（0~4） */
export const SCALE_LABELS = ['远景', '全景', '中景', '近景', '特写'] as const;

/** 景别档位提示词（v1.10 精简：仅标准术语，模型可直接理解） */
export const SCALE_PROMPTS = [
  '远景',
  '全景',
  '中景',
  '近景',
  '特写镜头',
] as const;

/** 景别按钮组（图一三档） */
export const SCALE_BUTTONS: { label: string; value: number }[] = [
  { label: '特写', value: 4 },
  { label: '中景', value: 2 },
  { label: '远景', value: 0 },
];

/** 水平方位角限定到 0~359 循环（0° 左边即 359°；旧数据的 -180~0 负值自动归一为正） */
export const clampAzimuth = (v: number): number => ((Math.round(v) % 360) + 360) % 360;

export const clampElevation = (v: number): number => Math.max(-30, Math.min(60, Math.round(v)));

export const clampScaleIndex = (v: number): number => Math.max(0, Math.min(4, Math.round(v)));

/** 景别限定到三档：远景(0)/中景(2)/特写(4)，任意 0~4 输入吸附最近档（历史 1 全景/3 近景 存档自动归档） */
export const snapScaleIndex = (v: number): number => Math.max(0, Math.min(4, Math.round(v / 2) * 2));

/** 将任意角度归一到 [-180, 180] 区间（拖拽累计与预设切换动画的最短路径计算用） */
export function normalizeAzimuth(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let v = deg % 360;
  if (v > 180) v -= 360;
  else if (v < -180) v += 360;
  return v === 0 ? 0 : v;
}

/** 最短路径角度差：从 from 转到 to 的带符号最短角度差（[-180, 180]） */
export function shortestAngleDelta(from: number, to: number): number {
  return normalizeAzimuth(to - from);
}

/** 按当前参数反查预设：与某预设三元组完全一致则返回该预设 id，否则视为「自定义」 */
export function matchPresetId(params: {
  azimuth: number;
  elevation: number;
  scaleIndex: number;
}): MultiAnglePresetId {
  const azimuth = clampAzimuth(params.azimuth);
  const elevation = clampElevation(params.elevation);
  const scaleIndex = clampScaleIndex(params.scaleIndex);
  const matched = ANGLE_PRESETS.find(
    (p) => p.azimuth === azimuth && p.elevation === elevation && p.scaleIndex === scaleIndex
  );
  return matched ? matched.id : 'custom';
}

export const DEFAULT_MULTI_ANGLE_PARAMS = {
  presetId: 'custom',
  azimuth: 0,
  elevation: 0,
  scaleIndex: 2,
  promptEnabled: false,
} as const;
